import type { FC } from 'react';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { FacetBadges } from '../../../../../front/src/thread/Category/FacetBadges';
import { TopicSubject } from '../../../../../front/src/thread/Topic/TopicSubject';
import type { PressArticlesBlockData } from '../../../../../front/src/types/layout';
import {
  ArticleMeta,
  getHeroSrc,
  HeroImage,
  pressMessages,
  SectionHead,
  StretchedTitleLink,
} from './pressShared';

interface Props {
  block: PressArticlesBlockData;
  className?: string;
}

const DEFAULT_LIMIT = 6;

/** "Le fil" — responsive grid of recent editorial articles. Media mode only. */
export const BlockPressFeed: FC<Props> = memo(function BlockPressFeed({ block }) {
  const limit = block.options?.limit ? parseInt(block.options.limit, 10) : DEFAULT_LIMIT;
  const articles = (block.data || []).slice(0, limit);
  if (!articles.length) return null;

  return (
    <section className="BlockPressFeed max-w-5xl mx-auto px-7 pt-16">
      <SectionHead
        title={block.options?.customTitle || <FormattedMessage {...pressMessages.feed} />}
        showLive
      />
      <div className="grid gap-x-9 gap-y-12 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
        {articles.map(article => (
          <div key={article.id} className="relative group flex flex-col">
            <div className="relative w-full aspect-[16/10] rounded object-cover bg-gray-100 mb-[15px] overflow-hidden">
              {getHeroSrc(article) && (
                <HeroImage
                  src={getHeroSrc(article) as string}
                  alt={article.hero?.alt ?? ''}
                  sizes="(max-width: 768px) 100vw, 400px"
                  className="object-cover"
                />
              )}
            </div>
            <h3 className="text-[21px] leading-[1.17] font-bold mb-2.5 group-hover:text-primary-base">
              <StretchedTitleLink slug={article.slug}>
                <TopicSubject subject={article.subject as string} />
              </StretchedTitleLink>
            </h3>
            <ArticleMeta
              author={article.author}
              date={article.createdAt}
              reactions={article.messagesCount}
            />
            {/* Tags sur UNE ligne : scroll horizontal (scrollbar masquée) + fondu à droite. */}
            <FacetBadges
              themes={article.themes}
              entities={article.entities}
              disableLink
              plain
              className="relative z-10 mt-2 flex w-full min-w-0 flex-nowrap items-center overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] [&::-webkit-scrollbar]:hidden"
            />
          </div>
        ))}
      </div>
    </section>
  );
});
